import React, { useState } from 'react';
import { useApi, fmtNum, fmtKST, holdOr } from '../hooks/useApi';
import BotAvatar from './BotAvatar';
import SyncFreshnessBadge from './SyncFreshnessBadge';

const RANK_COLORS = ['text-yellow-400', 'text-gray-300', 'text-amber-600'];

export default function BotLeaderboard({ compact = false }) {
  const { data, loading } = useApi('/api/bots/leaderboard');
  const { data: verdicts } = useApi('/api/bots/verdicts?limit=30');
  const [expandedBot, setExpandedBot] = useState(null);

  if (loading || !data) {
    return <div className="animate-pulse h-48 bg-gray-800 rounded-lg" />;
  }

  const sorted = [...data].sort((a, b) => (b.current_weight ?? -1) - (a.current_weight ?? -1));

  // 봇별 최근 verdict 그룹핑
  const verdictsByBot = {};
  if (verdicts) {
    for (const v of verdicts) {
      if (!verdictsByBot[v.bot_name]) verdictsByBot[v.bot_name] = [];
      if (verdictsByBot[v.bot_name].length < 5) verdictsByBot[v.bot_name].push(v);
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-400">봇 리더보드</h2>
        <SyncFreshnessBadge lastSyncedAt={sorted[0]?.last_synced_at} />
      </div>
      <div className="space-y-2">
        {sorted.map((bot, idx) => {
          const isOpen = expandedBot === bot.bot_name;
          const botVerdicts = verdictsByBot[bot.bot_name] || [];
          return (
            <div key={bot.bot_name}>
              <button
                className="w-full text-left flex items-center gap-3 py-1 hover:bg-gray-750 rounded transition-colors"
                onClick={() => !compact && setExpandedBot(isOpen ? null : bot.bot_name)}
              >
                <span className={`text-lg font-bold w-6 text-center ${RANK_COLORS[idx] || 'text-gray-500'}`}>
                  {idx + 1}
                </span>
                <BotAvatar botName={bot.bot_name} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">{bot.bot_name}</span>
                    <span className="text-xs text-gray-400">{holdOr(bot.current_weight, (v) => `${Number(v).toFixed(2)}%`)}</span>
                  </div>
                  {!compact && (
                    <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                      <span>거래 {bot.total_trades}건 (체결 {bot.closed_trades}건)</span>
                      <span>승률 {bot.win_rate == null ? '— (표본 없음)' : `${Number(bot.win_rate).toFixed(1)}%`}</span>
                      <span className={bot.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                        손익 {holdOr(bot.total_pnl, (v) => `₩${Number(v).toLocaleString()}`)}
                      </span>
                      {bot.insufficient_sample ? (
                        <span className="text-gray-600" title={`체결 ${bot.closed_trades}건 — 표본 부족`}>MDD 표본 부족</span>
                      ) : (
                        <span>MDD {holdOr(bot.max_drawdown, (v) => `${Number(v).toFixed(2)}%`)}</span>
                      )}
                    </div>
                  )}
                  {bot.current_weight != null && <div className="w-full bg-gray-700 rounded-full h-1 mt-1">
                    <div
                      className="bg-blue-500 h-1 rounded-full"
                      style={{ width: `${Math.min(bot.current_weight * 2.5, 100)}%` }}
                    />
                  </div>}
                </div>
              </button>

              {/* Recent verdicts timeline */}
              {isOpen && botVerdicts.length > 0 && (
                <div className="ml-10 mt-1 mb-2 pl-3 border-l border-gray-700 space-y-1">
                  {botVerdicts.map((v, i) => {
                    const dc = v.decision === 'BUY' ? 'text-green-400'
                      : v.decision === 'SELL' ? 'text-red-400' : 'text-gray-500';
                    return (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-600 min-w-[60px]">{fmtKST(v.verdict_at)}</span>
                        <span className={`font-medium min-w-[30px] ${dc}`}>{v.decision}</span>
                        <span className="text-gray-500">{v.confidence?.toFixed(0)}%</span>
                        <span className="text-gray-400 truncate">
                          {v.stock_name || v.ticker}
                          {v.market === 'US' ? ` ($)` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
